import React, { createContext, useCallback, useContext, useState } from 'react';
import Cookies from 'js-cookie';

const COOKIE_NAME = 'employee-auth-token';

function decodeJWT(token: string): Record<string, unknown> | null {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch {
        return null;
    }
}

function getRoleFromCookie(): string | null {
    const token = Cookies.get(COOKIE_NAME);
    if (!token) return null;
    return (decodeJWT(token)?.role as string) ?? null;
}

interface AuthContextProps {
    isAuthenticated: boolean;
    isSuperAdmin: boolean;
    login: () => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

export function AuthContextProvider({ children }: { children: React.ReactNode }) {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
        () => !!Cookies.get(COOKIE_NAME)
    );
    const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(
        () => getRoleFromCookie() === 'superadmin'
    );

    const login = useCallback(() => {
        setIsAuthenticated(true);
        setIsSuperAdmin(getRoleFromCookie() === 'superadmin');
    }, []);

    const logout = useCallback(() => {
        Cookies.remove(COOKIE_NAME);
        setIsAuthenticated(false);
        setIsSuperAdmin(false);
    }, []);

    return (
        <AuthContext.Provider value={{ isAuthenticated, isSuperAdmin, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
